"use client";

import { useT, useGameLocale } from "@/lib/i18n";
import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "@/i18n/navigation";
import { useAuth } from "@/app/contexts/AuthContext";
import { useToast } from "@/app/components/Toast";
import RunDropZone from "@/app/components/RunDropZone";
import ProfileStats from "@/app/components/ProfileStats";
import { SupporterBadge } from "@/app/components/SupporterBadge";
import { forgetFlair } from "@/lib/supporter-flair";
import MyChartsSection from "@/app/components/MyChartsSection";
const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
interface Run {
  run_hash: string;
  character: string;
  win: boolean;
  was_abandoned: boolean;
  ascension: number;
  game_mode: string;
  player_count: number;
  floors_reached: number;
  killed_by: string | null;
  username: string | null;
  submitted_at: string;
}
interface UploadResult {
  filename: string;
  status: "claimed" | "duplicate" | "error";
  detail?: string;
  run_hash?: string;
}
const SOURCE_LABELS: Record<string, string> = {
  patreon: "Patreon",
  kofi: "Ko-fi",
  overwolf: "Overwolf"
};
export default function ProfileClient() {
  const {
    user,
    loading
  } = useAuth();
  const lang = useGameLocale();
  const t = useT();
  const {
    toast
  } = useToast();
  const [runs, setRuns] = useState<Run[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [runsLoading, setRunsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<UploadResult[] | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{
    total: number;
    done: number;
    dupes: number;
    errors: number;
  } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [profilePrivate, setProfilePrivate] = useState<boolean | null>(null);
  const [thanksListed, setThanksListed] = useState<boolean | null>(null);
  const [themePublic, setThemePublic] = useState<boolean | null>(null);
  useEffect(() => {
    if (user) {
      setProfilePrivate(Boolean(user.profile_private));
      setThanksListed(Boolean(user.supporter?.listed));
      setThemePublic(Boolean(user.supporter?.theme_public));
    }
  }, [user]);
  const toggleThemePublic = async (next: boolean) => {
    const prev = themePublic;
    setThemePublic(next);
    try {
      const res = await fetch(`${API_BASE}/api/auth/theme`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          public: next
        })
      });
      if (!res.ok) throw new Error();
      forgetFlair(user?.username);
      toast(next ? t("Your theme now shows on your profile and runs.") : t("Your theme is only visible to you."), "success");
    } catch {
      setThemePublic(prev);
      toast(t("Network error"), "error");
    }
  };
  const toggleThanksListing = async (next_0: boolean) => {
    const prev_0 = thanksListed;
    setThanksListed(next_0);
    try {
      const res_0 = await fetch(`${API_BASE}/api/auth/thanks-listing`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          listed: next_0
        })
      });
      if (!res_0.ok) throw new Error();
      toast(next_0 ? t("Your name is now on the Thank You page.") : t("Your name is hidden from the Thank You page."), "success");
    } catch {
      setThanksListed(prev_0);
      toast(t("Network error"), "error");
    }
  };
  const togglePrivacy = async (next_1: boolean) => {
    const prev_1 = profilePrivate;
    setProfilePrivate(next_1);
    try {
      const res_1 = await fetch(`${API_BASE}/api/auth/profile-privacy`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          private: next_1
        })
      });
      if (!res_1.ok) throw new Error();
      toast(next_1 ? t("Your profile is now private.") : t("Your profile is now public."), "success");
    } catch {
      setProfilePrivate(prev_1);
      toast(t("Network error"), "error");
    }
  };
  const fetchRuns = useCallback(async (p: number) => {
    setRunsLoading(true);
    try {
      const res_2 = await fetch(`${API_BASE}/api/auth/runs?page=${p}&limit=20`, {
        credentials: "include"
      });
      if (res_2.ok) {
        const data = await res_2.json();
        setRuns(data.runs || []);
        setTotal(data.total || 0);
      }
    } catch {
      toast(t("Failed to load runs"), "error");
    } finally {
      setRunsLoading(false);
    }
  }, [toast, lang]);
  useEffect(() => {
    if (user) fetchRuns(page);
  }, [user, page, fetchRuns]);

  // Batches go up in chunks of 10, sequentially: one request for 100
  // files was a single point of failure (a proxy body cap or a dropped
  // connection lost the whole batch), and 10 chunks a minute is exactly
  // the endpoint's rate limit. A failed chunk is retried twice before its
  // files are reported as errors and the rest keep going.
  const UPLOAD_CHUNK = 10;
  const handleUpload = async (files: FileList | File[]) => {
    const all = Array.from(files);
    if (!all.length) return;
    setUploading(true);
    setUploadResults(null);
    const progress = {
      total: all.length,
      done: 0,
      dupes: 0,
      errors: 0
    };
    setUploadProgress({
      ...progress
    });
    const results: UploadResult[] = [];
    const summary = {
      claimed: 0,
      duplicates: 0,
      errors: 0
    };
    let signedOut = false;
    const send = async (chunk: File[]): Promise<Response> => {
      const formData = new FormData();
      chunk.forEach(f => formData.append("files", f));
      return fetch(`${API_BASE}/api/auth/runs/upload`, {
        method: "POST",
        credentials: "include",
        body: formData
      });
    };
    for (let i = 0; i < all.length && !signedOut; i += UPLOAD_CHUNK) {
      const chunk_0 = all.slice(i, i + UPLOAD_CHUNK);
      let res_3: Response | null = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          res_3 = await send(chunk_0);
        } catch {
          res_3 = null;
        }
        // Retry only what a retry can fix: network drops and 5xx.
        if (res_3 && (res_3.ok || res_3.status < 500)) break;
        await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
      }
      if (res_3?.ok) {
        const data_0 = await res_3.json();
        results.push(...(data_0.results || []));
        summary.claimed += data_0.summary?.claimed || 0;
        summary.duplicates += data_0.summary?.duplicates || 0;
        summary.errors += data_0.summary?.errors || 0;
      } else if (res_3?.status === 401) {
        signedOut = true;
      } else {
        const err = res_3 ? await res_3.json().catch(() => null) : null;
        const detail = res_3?.status === 413 ? t("Too many files or file too large") : err?.detail || (res_3 ? t("Upload failed") : t("Network error during upload"));
        chunk_0.forEach(f_0 => results.push({
          filename: f_0.name,
          status: "error",
          detail
        }));
        summary.errors += chunk_0.length;
      }
      progress.done = Math.min(all.length, i + chunk_0.length);
      progress.dupes = summary.duplicates;
      progress.errors = summary.errors;
      setUploadProgress({
        ...progress
      });
      setUploadResults([...results]);
    }
    if (signedOut) {
      toast(t("Please sign in to upload runs"), "error");
    } else {
      toast(`${summary.claimed} ${t("claimed")}, ${summary.duplicates} ${t("duplicates")}, ${summary.errors} ${t("errors")}`, summary.errors > 0 ? "error" : "success");
      if (summary.claimed > 0) {
        fetchRuns(1);
        setPage(1);
      }
    }
    setUploadProgress(null);
    setUploading(false);
  };
  const handleDelete = async (runHash: string) => {
    try {
      const res_4 = await fetch(`${API_BASE}/api/auth/runs/${runHash}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (res_4.ok) {
        toast(t("Run removed from your profile"), "success");
        setRuns(prev_2 => prev_2.filter(r_0 => r_0.run_hash !== runHash));
        setTotal(prev_3 => prev_3 - 1);
      } else if (res_4.status === 403) {
        toast(t("You do not own this run"), "error");
      } else if (res_4.status === 404) {
        toast(t("Run not found"), "error");
      } else {
        toast(t("Failed to delete run"), "error");
      }
    } catch {
      toast(t("Network error"), "error");
    } finally {
      setDeleteConfirm(null);
    }
  };
  const handleDeleteMany = async (hashes: string[]) => {
    try {
      const res_5 = await fetch(`${API_BASE}/api/auth/runs/bulk-delete`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          run_hashes: hashes
        })
      });
      if (!res_5.ok) {
        toast(t("Failed to delete run"), "error");
        return;
      }
      const body = (await res_5.json()) as {
        deleted: string[];
        failed: Record<string, string>;
      };
      const removed = new Set(body.deleted);
      if (removed.size > 0) {
        // Count what this update actually drops rather than what the server
        // reported, so a row a single delete already removed is not subtracted
        // from the total twice.
        setRuns(prev_4 => {
          const next_2 = prev_4.filter(r_1 => !removed.has(r_1.run_hash));
          const dropped = prev_4.length - next_2.length;
          if (dropped > 0) setTotal(current => Math.max(0, current - dropped));
          return next_2;
        });
      }
      const failedCount = Object.keys(body.failed ?? {}).length;
      if (failedCount > 0) {
        toast(t("Removed {n} runs, {failed} could not be removed", {
          n: removed.size,
          failed: failedCount
        }), "error");
      } else {
        toast(t("Removed {n} runs from your profile", {
          n: removed.size
        }), "success");
      }
    } catch {
      toast(t("Network error"), "error");
    }
  };
  if (loading) {
    return <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="h-8 w-48 bg-[var(--bg-card)] rounded animate-pulse" />
      </div>;
  }
  if (!user) {
    return <div className="max-w-5xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-4">
          {t("Sign in to view your profile")}
        </h1>
        <p className="text-[var(--text-secondary)]">
          {t("Connect your Steam or Discord account to see your runs and stats.")}
        </p>
      </div>;
  }
  const totalPages = Math.ceil(total / 20);
  return <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
      <h1 className="text-2xl font-bold text-[var(--text-primary)]">
        {user.username ? `${user.username}'s ${t("Profile")}` : t("Your Profile")}
      </h1>

      {/* Stats (includes My Runs as a tab) */}
      <section>
        <ProfileStats runs={runs} runsTotal={total} runsLoading={runsLoading} runsPage={page} runsTotalPages={totalPages} onPageChange={setPage} onDeleteRun={handleDelete} deleteConfirm={deleteConfirm} onDeleteConfirm={setDeleteConfirm} onDeleteRuns={handleDeleteMany} />
      </section>

      {/* Claim Runs */}
      <section>
        <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">
          {t("Claim Runs")}
        </h2>
        <RunDropZone onFiles={files_0 => handleUpload(files_0)} uploading={uploading} uploadProgress={uploadProgress} />

        {uploadResults && uploadResults.length > 0 && <div className="mt-3 space-y-1 max-h-40 overflow-y-auto">
            {uploadResults.map((r_2, i_0) => <div key={i_0} className={`text-xs px-3 py-1.5 rounded flex items-center justify-between ${r_2.status === "claimed" ? "bg-success/10 text-success" : r_2.status === "duplicate" ? "bg-warning/10 text-warning" : "bg-danger/10 text-danger"}`}>
                <span className="truncate">{r_2.filename}</span>
                <span className="shrink-0 ml-2">
                  {r_2.status === "error" ? r_2.detail : r_2.status}
                </span>
              </div>)}
          </div>}
      </section>

      {/* Saved charts */}
      <MyChartsSection />

      {/* Public profile */}
      {user.username && profilePrivate !== null && <section className="bg-[var(--bg-card)] rounded-lg border border-[var(--border-subtle)] p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-[var(--text-primary)]">
                {t("Public profile")}
              </h2>
              <p className="text-xs text-[var(--text-muted)] mt-0.5">
                {profilePrivate ? t("Your profile page is private. Your runs still appear on leaderboards.") : t("Anyone can view your stats and insights at your player page.")}
              </p>
              {!profilePrivate && <Link href={`/players/${encodeURIComponent(user.username)}`} className="inline-block mt-1 text-xs text-[var(--accent-gold)] hover:underline">
                  {t("View public profile")}
                </Link>}
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none shrink-0">
              <input type="checkbox" checked={profilePrivate} onChange={e => togglePrivacy(e.target.checked)} className="accent-[var(--accent-gold)] w-4 h-4" />
              <span className="text-sm text-[var(--text-secondary)]">
                {t("Private profile")}
              </span>
            </label>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border-subtle)] pt-4">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[var(--text-primary)]">
                {user.supporter?.active ? t("You're a supporter, thank you. Ads are off for you.") : t("Supporters see no ads on the site.")}
              </div>
              <p className="text-xs text-[var(--text-muted)]">
                {user.supporter?.active ? user.supporter.sources.map(s => SOURCE_LABELS[s.source] ?? s.source).join(" · ") : t("Subscribe on Patreon, become a monthly Ko-fi member, or subscribe through the Overwolf overlay.")}
              </p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none shrink-0">
              <input type="checkbox" checked={Boolean(thanksListed)} onChange={e_0 => toggleThanksListing(e_0.target.checked)} className="accent-[var(--accent-gold)] w-4 h-4" />
              <span className="text-sm text-[var(--text-secondary)]">
                {t("List me on the Thank You page")}
              </span>
            </label>
          </div>
          {user.supporter?.active && <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border-subtle)] pt-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[var(--text-primary)]">
                  {t("Your theme")}{" "}
                  <SupporterBadge theme={user.supporter.theme} className="ml-1" />
                </div>
                <p className="text-xs text-[var(--text-muted)]">
                  {user.supporter.theme ? t("Pick a character or a custom colour from the theme menu in the top bar. When shown, people see your colours on your profile, your runs and your replays, and your badge everywhere your name appears.") : t("Pick a character or a custom colour from the theme menu in the top bar to set your theme.")}
                </p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none shrink-0">
                <input type="checkbox" checked={Boolean(themePublic)} onChange={e_1 => toggleThemePublic(e_1.target.checked)} className="accent-[var(--accent-gold)] w-4 h-4" />
                <span className="text-sm text-[var(--text-secondary)]">
                  {t("Show my theme to others")}
                </span>
              </label>
            </div>}
        </section>}
    </div>;
}